<template>
	<ProcessCreatePage>
		<Header v-if="!isRentalCarContractLater" :title="title">
			<p>{{ $t('required_data_red_stars') }}</p>
		</Header>

		<div class="main">
			<form>
				<div class="md:flex md:flex-wrap w-full gap-2">
					<BaseCheckbox
						ref="BaseInput.isIdentificationCardChecked"
						:required="true"
						:label="$t('identity_card_checked')"
						:error-message="$t('identity_card_needs_to_be_checked')"
						v-model="rentalCarContract.isIdentificationCardChecked"
						:disabled="disabled"
						class="w-1/3"
					/>

					<BaseCheckbox
						class="w-1/3"
						ref="BaseInput.isDriverLicenseChecked"
						:required="true"
						:label="$t('drivers_license_checked')"
						:error-message="$t('drivers_license_needs_to_be_checked')"
						v-model="rentalCarContract.isDriverLicenseChecked"
						:disabled="disabled"
					/>
				</div>
				<div class="flex flex-col">
					<div class="w-full">
						<div class="flex">
							<BaseDatepicker
								ref="BaseInput-rentalCarContract.rentStartDate"
								:required="true"
								:label="$t('rental_start')"
								type="text"
								error-behavior="blur"
								v-model="rentalCarContract.rentStartDate"
								class="w-full md:w-1/3 pr-3"
								:disabled="disabled"
							/>
							<BaseInput
								ref="Input.returnTime"
								:required="true"
								class="w-full md:w-1/3 pl-1"
								:label="$t('handover_time')"
								placeholder="17:00"
								type="tel"
								v-model="rentalStartTime"
								maxlength="5"
								:mask="['NN:NN', 'N:NN']"
								validation="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
								:error-message="$t('enter_valid_time')"
								:disabled="disabled"
							/>
						</div>
						<div class="flex flex-row gap-4">
							<BaseInputNumber
								ref="BaseInput-rentalCarContract.kilometrage"
								:required="true"
								:label="$t('handover_mileage')"
								type="tel"
								class="w-full md:w-1/3"
								v-model="rentalCarContract.kilometrage"
								min-number="1"
								:error-message="$t('km_bigger_zero')"
								:disabled="disabled"
							/>
							<BaseInput
								v-if="damageCase && damageCase.vehicle"
								:value="
									damageCase.replacementRentalCarClass === 0
										? $t('cannot_be_determined')
										: damageCase.replacementRentalCarClass
										? $t('rentalcar_class') + ' ' + damageCase.replacementRentalCarClass
										: $t('rental_car_class_undefined')
								"
								:label="$t('accident_vehicle')"
								:disabled="true"
								class="w-full md:w-1/3"
							/>

							<BaseSelect
								ref="BaseSelect.vehicle"
								:required="true"
								:label="$t('rental_car_license_plate')"
								:options="getListOfLicensePlates"
								v-model="rentalCarContract.vehicle"
								v-if="!disabled"
								class="w-full md:w-1/3"
							/>

							<BaseInput
								ref="Input.rentalCarLicensePlate"
								:label="$t('rental_car_license_plate')"
								:required="true"
								type="text"
								:value="licensePlateText"
								:disabled="disabled"
								v-if="disabled"
								class="w-full md:w-1/3"
							/>
						</div>
					</div>
					<div class="justify-between">
						<div class="flex font-bold">
							{{ $t('one_time_options') }}
						</div>
						<div class="flex grid grid-cols-2 my-4">
							<div v-for="rentalCarExtra in rentalCarContractOnceExtras" :key="rentalCarExtra.id">
								<BaseCheckbox
									:ref="'BaseInput.' + rentalCarExtra.id"
									:label="rentalCarExtra.label"
									v-model="rentalCarExtra.isEnabled"
									:disabled="false"
								/>
							</div>
						</div>
					</div>
					<div class="justify-between">
						<div class="flex font-bold">
							{{ $t('options_with_daily_costs') }}
						</div>
						<div class="flex grid grid-cols-2 my-4">
							<div v-for="rentalCarExtra in rentalCarContractDailyExtras" :key="rentalCarExtra.id">
								<BaseCheckbox
									:ref="'BaseInput.' + rentalCarExtra.id"
									:label="rentalCarExtra.label"
									v-model="rentalCarExtra.isEnabled"
									:disabled="false"
								/>
							</div>
						</div>
					</div>
				</div>

				<BaseInput ref="BaseInput.damage" type="text" :label="$t('old_damages_rental_car')">
					<textarea class="resize-y w-full shadow-m h-32" v-model="rentalCarContract.damage" :disabled="disabled"></textarea>
				</BaseInput>
			</form>
		</div>

		<Footer
			v-if="!isRentalCarContractLater"
			@backButtonClick="backButton"
			:is-back-button="showBackButton"
			@continueButtonClick="continueButtonClick"
			:is-skippable="false"
		/>
		<div v-if="isRentalCarContractLater && !disabled">
			<BaseButton @click="saveRentalCarContract" class="btn-blue">{{ $t('general.next') }}</BaseButton>
		</div>
	</ProcessCreatePage>
</template>

<script>
import Footer from '@/components/damagecases/Footer';
import Header from '@/components/damagecases/Header';
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber';
import Validate from '@/mixins/Validate';
import BaseButton from '@/components/forms/buttons/BaseButton';
import { getTimeFromDate } from '@/plugins/Utils';
import stateMerge from 'vue-object-merge';
import Authentication from '@/mixins/Authentication';
import ProcessCreatePage from '@/views/management/Process/ProcessCreatePage';
import RentalCarExtraCostsRepository from '@/api/repositories/RentalCarExtraCostsRepository';

export default {
	name: 'RentalCarContract',
	components: { ProcessCreatePage, BaseButton, BaseInputNumber, BaseDatepicker, BaseCheckbox, BaseSelect, BaseInput, Header, Footer },
	mixins: [Validate, Authentication],
	props: {
		isRentalCarContractLater: { type: Boolean, default: false },
		display: {},
		cars: { required: true },
		damageCase: {},
		disabled: { type: Boolean, default: false },
		existingContract: {},
		title: {
			default: function() {
				return this.$t('generate_form_rental_car');
			},
		},
		showBackButton: {
			default: false,
		},
	},
	data() {
		return {
			rentalStartTime: getTimeFromDate(new Date()),
			rentalCarContract: {
				isDriverLicenseChecked: null,
				isIdentificationCardChecked: null,
				rentStartDate: new Date(),
				kilometrage: null,
				vehicle: null,
				extras: [],
				damage: '',
			},
			rentalCarContractDailyExtras: [],
			rentalCarContractOnceExtras: [],
		};
	},
	created() {
		if (this.existingContract) {
			stateMerge(this.rentalCarContract, this.existingContract);
			this.rentalCarContract.rentStartDate = new Date(this.rentalCarContract.rentStartDate * 1000);
			this.rentalStartTime = this.rentalCarContract.rentStartDate.getHours() + ':' + this.rentalCarContract.rentStartDate.getMinutes();
		}
		this.getRentalCarExtras();
	},
	methods: {
		backButton($event) {
			this.$emit('previous', $event);
		},
		setChecked: function(type) {
			if (type === 'drivingLicense') this.rentalCarContract.isDriverLicenseChecked = true;
			if (type === 'identificationCard') this.rentalCarContract.isIdentificationCardChecked = true;
		},
		continueButtonClick: function($event) {
			if (this.validate()) {
				this.rentalCarContract.extras = this.rentalCarContractOnceExtras.concat(this.rentalCarContractDailyExtras);
				this.$emit('next', {
					rentalCarContract: {
						...this.rentalCarContract,
						rentStartDate: this.getTimeAsUnixTimeStamp,
						vehicle: this.rentalCarContract.vehicle.value,
					},
				});
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
		getRentalCarExtras() {
			new RentalCarExtraCostsRepository()
				.getAll(true)
				.then(response => {
					this.rentalCarContractDailyExtras = response.data.daily;
					this.rentalCarContractOnceExtras = response.data.once;
				})
				.catch(e => {
					console.log(e);
				});
		},
		saveRentalCarContract() {
			if (this.validate()) {
				this.$emit('saveContract', {
					rentalCarContract: {
						...this.rentalCarContract,
						rentStartDate: this.getTimeAsUnixTimeStamp,
						vehicle: this.rentalCarContract.vehicle.value,
					},
				});
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
	},
	computed: {
		licensePlateText() {
			if (this.rentalCarContract?.vehicle.length) {
				return this.rentalCarContract?.vehicle[0].value.licensePlate.text;
			}
			return this.rentalCarContract?.vehicle?.licensePlate?.text;
		},
		getTimeAsUnixTimeStamp: function() {
			if (this.$refs['Input.returnTime'].validate()) {
				const time = this.rentalStartTime.split(':');

				this.rentalCarContract.rentStartDate.setHours(time[0]);
				this.rentalCarContract.rentStartDate.setMinutes(time[1]);
			}

			return parseInt(this.rentalCarContract.rentStartDate.getTime() / 1000);
		},
		getListOfLicensePlates() {
			const plates = [];
			if (this.cars) {
				for (let i = 0; i < this.cars.length; i++) {
					const isRented =
						this.cars[i].vehicle && this.cars[i].vehicle.rentalContracts && this.cars[i].damageCase.id !== this.damageCase.id
							? ' – derzeit in Vermietung'
							: '';
					if (this.cars[i].vehicle.rentalCarClass) {
						plates.push({
							value: this.cars[i].vehicle,
							grayed: isRented,
							$isDisabled: isRented,
							display:
								this.cars[i].vehicle.licensePlate.text +
								' (' +
								this.cars[i].vehicle.manufacturer +
								' ' +
								this.cars[i].vehicle.model +
								') - Klasse ' +
								this.cars[i].vehicle.rentalCarClass +
								isRented,
						});
					}
				}

				return plates;
			}
			return null;
		},
	},
	watch: {
		getListOfLicensePlates() {
			if (this.existingContract) {
				this.rentalCarContract.vehicle = this.getListOfLicensePlates.filter(car => {
					return this.existingContract.vehicle.id === car.value.id;
				});
			}
		},
		display: function() {
			if (this.display) {
				if (!this.rentalCarContract.isDriverLicenseChecked) {
					this.rentalCarContract.isDriverLicenseChecked = this.damageCase.drivingLicenseImage !== null;
				}

				if (!this.rentalCarContract.isIdentificationCardChecked) {
					this.rentalCarContract.isIdentificationCardChecked = this.damageCase.personalIDImage !== null;
				}
			}
		},
	},
};
</script>

<style lang="sass" scoped></style>
